import React, { useState } from 'react';
import { strings } from '/imports/strings';
import { ModelClass } from '/imports/types';
import { RichSelect } from '/imports/ui/RichSelect';
import { toastOnError } from '/imports/ui/utils';
import { ProjectManagementParty } from '/imports/api/projectManagement';

/**
 * Registry picker for a project's owner, EPC or utility: choose an existing company, or create the name
 * being searched for right there. A quick-create stores just the name — the server's create hook then
 * looks the logo up in the background, which is why nothing here asks for one.
 *
 * Lives in its own file because both project forms use it: the project page's Edit modal and the
 * pipeline's New Project form, which have to offer the same fields.
 */
export const PartyPicker = (props: {
  modelClass: ModelClass<ProjectManagementParty, any>;
  parties: ProjectManagementParty[];
  value?: string;
  onChange: (id?: string) => void;
}) => {
  const { modelClass, parties, value, onChange } = props;
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const selected = parties.find((p) => p._id === value);
  const typed = search.trim();
  // Only offer a create for a name the registry does not already hold, case-insensitively.
  const canCreate = !!typed && !parties.some((p) => p.name.toLowerCase() === typed.toLowerCase());
  const create = toastOnError(async () => {
    if (!typed || creating) return;
    setCreating(true);
    try {
      const _id = await modelClass.createMethod!.call({ name: typed });
      onChange(_id);
      setSearch('');
      // The panel kept focus for the click above; dropping it closes the list on the new selection.
      (document.activeElement as HTMLElement | null)?.blur();
    }
    finally {
      setCreating(false);
    }
  });
  return (
    <RichSelect
      listMethod={modelClass.listMethod}
      listQuery={{ active: true } as any}
      mapToValue={(p: ProjectManagementParty) => ({ value: p._id, label: p.name })}
      preSelectedOption={selected ? { value: selected._id, label: selected.name } : undefined}
      value={value}
      clearable
      onSearch={(v) => setSearch(v || '')}
      onChange={(option) => onChange(option.value || undefined)}
      onClear={() => onChange(undefined)}
      optionsFooter={canCreate
        ? <button className="link-btn party-create" onClick={() => create()}>{creating ? strings.Loading() : strings.CreateNamed(typed)}</button>
        : undefined}
    />
  );
};
